"use client";

import { useEffect, useState } from "react";
import { useFormState } from "react-dom";
import { useTranslations } from "next-intl";
import { useParams } from "next/navigation";
import { Input } from "@urbansportsclub/components/Input";
import { sendEvent, toTitleCase } from "@/lib/utils";
import { useRouter } from "@/lib/i18n/routing";
import { validateEmail } from "../actions";
import SubmitButton from "./submit-button";
import { EVENTS, ITEMS } from "@/constants/tracking";

interface ErrorDetails {
  trackingError: string;
  errorMessageI18nKey: string;
}

const formatErrorData = (formStateError: string): ErrorDetails => {
  const errorMap: Record<string, ErrorDetails> = {
    "generic-email": {
      trackingError: "Generic email error",
      errorMessageI18nKey: "inputError",
    },
    "network-error": {
      trackingError: "Network error",
      errorMessageI18nKey: "networkError",
    },
    default: {
      errorMessageI18nKey: "inputFormatError",
      trackingError: "Incorrect email format",
    },
  };

  return errorMap[formStateError] || errorMap.default;
};

export default function EmailForm() {
  const t = useTranslations("landing");
  const t_urls = useTranslations("urls");
  const { push } = useRouter();
  const { lang } = useParams<{ lang: string }>();

  const [emailInputVal, setEmailInputVal] = useState<string>("");
  const [emailInputErrorKey, setEmailInputErrorKey] = useState<
    string | undefined
  >();
  const [formSubmitting, setFormSubmitting] = useState<boolean>(false);

  /**
   * Form State
   */
  const [formState, formAction] = useFormState(validateEmail, undefined);

  useEffect(() => {
    if (!formState) {
      return;
    }

    setFormSubmitting(true);
    if (formState.success === true) {
      sendEvent({
        event: EVENTS.buttonClicked,
        item: ITEMS.submitEmailAddress,
        details: "email address submission",
        origin: "B2E Activation",
      });

      setTimeout(() => {
        push("/employee-offer/email-verification");
        setFormSubmitting(false);
      }, 1500);

      return;
    }

    if (formState.error) {
      if (formState.error === "failed-to-get-company") {
        sendEvent({
          event: EVENTS.actionFailed,
          error_message: "Email domain not found",
          item: ITEMS.submitEmailAddress,
          details: "email address submission",
          origin: "B2E Activation",
        });

        setTimeout(() => {
          push("/employee-offer/company-referral");
        }, 1500);

        return;
      }

      const { trackingError, errorMessageI18nKey } = formatErrorData(
        formState.error,
      );

      sendEvent({
        event: EVENTS.actionFailed,
        error_message: trackingError,
        item: ITEMS.submitEmailAddress,
        details: "email address submission",
        origin: "B2E Activation",
      });

      setEmailInputErrorKey(errorMessageI18nKey);
      setFormSubmitting(false);
    }
  }, [formState, lang, push]);

  return (
    <form
      action={formAction}
      className="mt-10 mb-4 rounded sm:mt-8"
      id="email-form"
      autoComplete="off"
    >
      <div className="mb-4">
        <label
          className={`text-md ${emailInputErrorKey ? "text-system-error-500" : "text-black-midnight-500"} mb-2 block font-bold`}
          htmlFor="email"
        >
          {t("formLabel")}
        </label>

        <Input
          className={`${emailInputErrorKey ? "text-system-error-500" : "text-black-midnight-500"} bg-grey-light-500 w-full appearance-none rounded px-3 leading-tight focus:px-[11px] focus:outline-none`}
          id="email"
          name="email"
          type="email"
          error={Boolean(emailInputErrorKey)}
          placeholder=""
          onChange={e => {
            setEmailInputVal(e.target.value);
            setEmailInputErrorKey("");
          }}
        />

        {emailInputErrorKey && (
          <p className="text-system-error-500 mt-1">{t(emailInputErrorKey)}</p>
        )}
      </div>

      <input type="hidden" name="language" value={lang} />

      <SubmitButton
        inputError={emailInputErrorKey}
        inputVal={emailInputVal}
        formSubmitting={formSubmitting}
        text={t("ctaDefaultText")}
        loadingText={t("ctaLoadingText")}
      />

      {formState?.error === "generic-email" && (
        <div className="mt-4 inline-flex w-full items-center justify-center">
          <a
            className="text-blue-respect-500 text-center text-base font-semibold"
            rel="noopener noreferrer"
            target="_blank"
            href={t_urls("b2cPricingPage", { lang })}
          >
            {toTitleCase(t("b2cPricingPageTitle"))}
          </a>
        </div>
      )}
    </form>
  );
}
